'use client';

import React from 'react';
import Link from 'next/link';
import { AlertCircle, ChevronRight } from 'lucide-react';
import BranchTag from '@/components/ui/BranchTag';
import { useTeam } from '@/context/TeamContext';
import { useNeedsAttention } from '@/hooks/useNeedsAttention';

type Branch = 'live_events' | 'studio' | 'outreach' | 'internal';

export default function UrgentGapsWidget() {
  const { activeTeam } = useTeam();
  const { items, loading } = useNeedsAttention();

  const isLeadership = activeTeam.id === 'leadership';

  // Show only urgent unresolved items relevant to this team
  const urgentGaps = items.filter((item) => {
    if (!item.is_urgent) return false;
    if (isLeadership) return true;
    return activeTeam.branches.includes(item.branch as Branch);
  });

  return (
    <div className="bg-white rounded-xl border border-stone-100 shadow-card overflow-hidden">
      <div className="px-4 py-3 border-b border-stone-100 flex items-center gap-2">
        <AlertCircle size={14} className="text-red-400" />
        <h3 className="text-[13px] font-semibold text-slate-700">
          {isLeadership ? 'Urgent Gaps — All Teams' : `Urgent Gaps · ${activeTeam.label}`}
        </h3>
        <span className="ml-auto text-[11px] font-semibold bg-red-100 text-red-600 px-1.5 py-0.5 rounded-full">
          {loading ? '…' : urgentGaps.length}
        </span>
      </div>

      {loading ? (
        <div className="px-4 py-6 flex items-center justify-center">
          <div className="w-4 h-4 border-2 border-red-100 border-t-red-400 rounded-full animate-spin" />
        </div>
      ) : urgentGaps.length === 0 ? (
        <div className="px-4 py-6 text-center">
          <p className="text-[13px] text-slate-400">No urgent gaps for this team.</p>
        </div>
      ) : (
        <div className="divide-y divide-stone-50">
          {urgentGaps.slice(0, 5).map((gap) => (
            <div key={gap.id} className="px-4 py-3">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p className="text-[13px] font-medium text-slate-800 leading-snug">{gap.title}</p>
                  <p className="text-[11px] text-slate-400 mt-0.5 truncate">{gap.related}</p>
                  <div className="flex items-center gap-2 mt-1.5">
                    <BranchTag branch={gap.branch as Branch} size="xs" />
                    <span className="text-[10px] font-medium text-red-600">{gap.due_label ?? 'Urgent'}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="px-4 py-2.5 border-t border-stone-50">
        <Link
          href="/needs-attention"
          className="flex items-center gap-1 text-[12px] font-medium text-indigo-600 hover:text-indigo-700 transition-colors"
        >
          View all gaps
          <ChevronRight size={13} />
        </Link>
      </div>
    </div>
  );
}